import React from 'react';
import { ActiveTab } from './utils/types';
import { canViewDocuments, canViewUsers } from './utils/permissions';

interface TabsNavigationProps {
  activeTab: ActiveTab;
  onTabChange: (tab: ActiveTab) => void;
  currentUserRole: string;
  onExport: () => void;
  onExportUsers: () => void;
  onShowFormulaire: () => void;
}

const TabsNavigation: React.FC<TabsNavigationProps> = ({
  activeTab,
  onTabChange,
  currentUserRole,
  onExport,
  onExportUsers,
  onShowFormulaire
}) => {
  return (
    <div className="flex space-x-1 mb-6 overflow-x-auto pb-2">
      {canViewDocuments(currentUserRole) && (
        <TabButton
          active={activeTab === 'documents'}
          onClick={() => onTabChange('documents')}
          icon="documents"
          label="Documents de voyages"
        />
      )}
      {canViewDocuments(currentUserRole) && (
        <TabButton
          active={activeTab === 'transferts'}
          onClick={() => onTabChange('transferts')}
          icon="transfer"
          label="Dossiers de transferts"
        />
      )}
      {canViewUsers(currentUserRole) && (
        <TabButton
          active={activeTab === 'users'}
          onClick={() => onTabChange('users')}
          icon="users"
          label="Utilisateurs"
        />
      )}
      
      <div className="ml-auto flex space-x-2 flex-wrap justify-end gap-2">
        {(currentUserRole === 'auditeur' || currentUserRole === 'conformité') && (activeTab === 'documents' || activeTab === 'transferts') && (
          <ActionButton
            onClick={onExport}
            icon="export"
            label="Exporter Excel"
            color="green"
          />
        )}
        
        {currentUserRole === 'super_admin' && activeTab === 'users' && (
          <ActionButton
            onClick={onExportUsers}
            icon="export"
            label="Exporter Utilisateurs"
            color="green"
          />
        )}

        {currentUserRole === 'superviseur' && (
          <ActionButton
            onClick={onShowFormulaire}
            icon="form"
            label="Formulaire client"
            color="green"
          />
        )}
      </div>
    </div>
  );
};

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: string;
  label: string;
}

const TabButton: React.FC<TabButtonProps> = ({ active, onClick, icon, label }) => {
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'documents':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />;
      case 'transfer':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />;
      case 'users':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />;
      default:
        return null;
    }
  };

  return (
    <button
      onClick={onClick}
      className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg transition-all duration-300 flex items-center flex-shrink-0 ${
        active
          ? 'bg-blue-600 text-white shadow-lg'
          : 'bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-600'
      }`}
    >
      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {getIcon(icon)}
      </svg>
      {label}
    </button>
  );
};

interface ActionButtonProps {
  onClick: () => void;
  icon: string;
  label: string;
  color: 'green' | 'blue' | 'red';
}

const ActionButton: React.FC<ActionButtonProps> = ({ onClick, icon, label, color }) => {
  const colorClasses = {
    green: 'bg-green-600 hover:bg-green-700',
    blue: 'bg-blue-600 hover:bg-blue-700',
    red: 'bg-red-600 hover:bg-red-700'
  };

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'export':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />;
      case 'form':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />;
      default:
        return null;
    }
  };

  return (
    <button
      onClick={onClick}
      className={`px-4 sm:px-6 py-2 sm:py-3 ${colorClasses[color]} text-white rounded-lg hover:shadow-lg transition-all duration-300 flex items-center flex-shrink-0`}
    >
      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {getIcon(icon)}
      </svg>
      {label}
    </button>
  );
};

export default TabsNavigation;
